import { useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';
import { classifyChanges } from '@components/ncFileVersions/components/NCFileDiffView/helpers/classifyChanges';
import { ChangeSummaryEntry } from '@components/ncFileVersions/components/NCFileDiffView/types';
import { ChangeSummaryCategory, MAX_LISTED_PER_CATEGORY } from '@components/ncFileVersions/components/NCFileDiffView/constants/classifyChanges.constants';
import {
  CATEGORY_DISPLAY_ORDER,
  ENTRY_GRID_GAP,
  ENTRY_GRID_MAX_COLUMNS,
  ENTRY_GRID_MIN_ITEM_WIDTH,
} from '../constants/changeSummaryPanel.constants';
import { scale } from '@components/frame';
import { CategoryGroup, UseManageChangeSummaryPanelParams, UseManageChangeSummaryPanelResult } from '@components/ncFileVersions/components/NCFileDiffView/ChangeSummaryPanel/types';

export const useManageChangeSummaryPanel = ({
  preparedListItems,
  onJumpToChange,
  dialect,
}: UseManageChangeSummaryPanelParams): UseManageChangeSummaryPanelResult => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const summary = useMemo(() => classifyChanges(preparedListItems, dialect), [preparedListItems, dialect]);

  const hasChanges = summary.totalChanges > 0;

  const visibleChipCategories = useMemo(
    () =>
      CATEGORY_DISPLAY_ORDER
        .map((category) => ({ category, count: summary.countsByCategory[category] }))
        .filter(({ count }) => count > 0),
    [summary.countsByCategory]
  );

  const visibleCategoryGroups = useMemo<CategoryGroup[]>(() => {
    const grouped = new Map<ChangeSummaryCategory, ChangeSummaryEntry[]>();
    summary.entries.forEach((entry) => {
      const bucket = grouped.get(entry.category) ?? [];
      bucket.push(entry);
      grouped.set(entry.category, bucket);
    });

    return CATEGORY_DISPLAY_ORDER
      .filter((category) => grouped.has(category))
      .map((category) => {
        const bucket = grouped.get(category) ?? [];
        return { category, entries: bucket.slice(0, MAX_LISTED_PER_CATEGORY), totalCount: bucket.length };
      });
  }, [summary.entries]);

  const toggleExpanded = useCallback(() => {
    if (hasChanges) {
      setIsExpanded((prev) => !prev);
    }
  }, [hasChanges]);

  const handleEntryPress = useCallback((entry: ChangeSummaryEntry) => onJumpToChange(entry), [onJumpToChange]);

  const handleExpandedLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;
    setContainerWidth((prev) => (Math.abs(prev - width) > 1 ? width : prev));
  }, []);

  const expandedItemWidth = useMemo(() => {
    if (containerWidth <= 0) {
      return 0;
    }
    const gap = scale(ENTRY_GRID_GAP);
    const columns = Math.max(1, Math.min(ENTRY_GRID_MAX_COLUMNS, Math.floor(containerWidth / ENTRY_GRID_MIN_ITEM_WIDTH)));
    return Math.floor((containerWidth - gap * (columns - 1)) / columns);
  }, [containerWidth]);

  return {
    isExpanded,
    toggleExpanded,
    summary,
    hasChanges,
    visibleCategoryGroups,
    visibleChipCategories,
    expandedItemWidth,
    handleExpandedLayout,
    handleEntryPress,
  };
};
